import type { DisplayRecord } from '../../../src/property-records';
import type { OfficialTaxBills } from '../../../src/property-view-types';
import { listTaxBills, taxBillInstallments } from './treasurer';

const money = (value: number | null) =>
  value === null
    ? 'Not published'
    : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);

/** Bill history uses the published issue date, never the roll year or a payment deadline. */
export function taxBillRecords(taxBills?: OfficialTaxBills): DisplayRecord[] {
  return listTaxBills(taxBills?.snapshot).map((bill) => {
    const installments = taxBillInstallments(bill);
    const paid = installments.length > 0 && installments.every((item) => item.status === 'paid');
    const status = paid
      ? 'Paid'
      : installments.some((item) => item.status === 'unpaid')
        ? 'Unpaid'
        : 'Payment status unknown';
    return {
      id: bill.billNumber,
      kind: 'tax-bill',
      status,
      date: bill.detail?.issuedOn ?? '',
      description: bill.label || `Tax bill ${bill.billNumber}`,
      address: taxBills?.snapshot?.account?.address ?? '',
      sourceUrl: bill.detailUrl,
      archived: paid,
      pages: [
        {
          title: 'Tax bill',
          fields: {
            'Bill number': bill.billNumber,
            'Ownership period': bill.period.label,
            ...(bill.year !== null ? { 'Roll year': String(bill.year) } : {}),
            ...(bill.paymentPlanBillNumber ? { 'Payment plan': bill.paymentPlanBillNumber } : {}),
            ...(taxBills?.status === 'stale'
              ? { 'Source check': 'The latest check failed; these are the last retrieved values.' }
              : {}),
          },
          tables: [],
          links: [],
        },
        ...installments.map((item) => ({
          title: item.label,
          fields: {
            Status: item.status,
            'Amount due': money(item.amountDue),
            'Paid amount': money(item.paidAmount),
            'Paid on': item.paidOn ?? 'Not published',
            'Delinquent after': item.delinquentAfter ?? 'Not published',
          },
          tables: [],
          links: [],
        })),
        ...Object.entries(bill.detail?.sections ?? {}).map(([title, fields]) => ({
          title,
          fields,
          tables: [],
          links: [],
        })),
      ],
    };
  });
}
